import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
	COMMENTS_PAGE_SIZE,
	addItemComment,
	deleteItemComment,
	editItemComment,
	listItemComments,
} from '../services/itemComments';
import { commentRefusal } from '../utils/commentRefusals';
import { normalizeId } from '../utils/ids';

/** The most pages "load the rest" will walk, so a runaway total cannot loop forever. */
const MAX_PAGES = 100;

const EMPTY = Object.freeze({
	itemId: null,
	status: 'idle', // 'idle' | 'loading' | 'ready' | 'error' | 'hidden'
	loaded: false, // a first page has arrived for this item (an error after it keeps what is shown)
	hidden: null, // 'gone' | 'off' when status is 'hidden'
	error: null,
	comments: [],
	next: 0, // how many the server has handed over (the next skip)
	total: 0,
	canComment: false,
	strictAuditing: false,
	loadingMore: false,
});

const idOf = comment => normalizeId(comment?._id);

/** Page `more` after `have`, by id: a comment that moved between pages is kept once. */
function mergePage(have, more) {
	const seen = new Set(have.map(idOf));
	return [...have, ...more.filter(comment => !seen.has(idOf(comment)))];
}

/**
 * One item's comments (KPE-005, KPR-306; services/itemComments.js has the
 * contract). The server is the authority for everything that decides what a
 * person sees: `canComment` (the composer), `strictAuditing` (deleted rows)
 * and each comment's `canEdit` / `canDelete` (Edit and Delete). Nothing here
 * guesses them from the card or the caller.
 *
 * Reads the first page when `enabled` and the item is saved, then pages of 50
 * on `loadMore`. The answer is keyed by item, so one that lands after the
 * section moved on to another item is dropped. A 404 (the item cannot be
 * read, or is a record reached by reference) or a 409 `comments_off` (the card
 * stopped allowing them since this session read it) leaves `status: 'hidden'`:
 * the section draws nothing and says nothing.
 *
 * Edit and Delete follow each comment's `canEdit` / `canDelete` (KPR-801):
 * the API's answer of who may, a note from before comments included (a
 * manager deletes it, nobody edits it). The section reads them.
 *
 * A delete names the strict-auditing mode the list showed (KPR-789). When the
 * card's mode has changed since, the API writes nothing and answers 409
 * `comment_audit_changed` with the mode now: the hook adopts it, reads the list
 * again and resolves `{ ok: false, kind: 'audit_changed', strictAuditing }` so
 * the caller can ask the person to confirm once more under the new mode.
 *
 * Writes move the state on the SERVER's answer, never ahead of it. Each
 * resolves `{ ok: true, comment }` or `{ ok: false, kind, text }` (utils/
 * commentRefusals.js), so the caller keeps what was typed and shows the
 * sentence. A refusal that means "what you see is stale" refreshes the list.
 *
 * With strict auditing off a deleted comment is dropped on sight, even one
 * the server should not have sent: that mode shows no deleted rows.
 */
export default function useItemComments({ itemId, enabled = true }) {
	const id = normalizeId(itemId);
	const active = enabled && Boolean(id) && id !== '-1';
	const [state, setState] = useState(EMPTY);
	const [reloadKey, setReloadKey] = useState(0);
	const [busy, setBusy] = useState(null); // { kind: 'add' | 'edit' | 'delete', id? }

	// The item the section currently describes: async answers compare to it.
	const currentRef = useRef(null);
	currentRef.current = active ? id : null;
	const stateRef = useRef(state);
	stateRef.current = state;
	const busyRef = useRef(null);

	useEffect(() => {
		if (!active) {
			setState(EMPTY);
			return undefined;
		}
		let cancelled = false;
		setState(prev => (prev.itemId === id
			? { ...prev, status: prev.status === 'ready' ? 'ready' : 'loading', error: null }
			: { ...EMPTY, itemId: id, status: 'loading' }));
		listItemComments(id, { skip: 0, limit: COMMENTS_PAGE_SIZE })
			.then(page => {
				if (cancelled) return;
				setState({
					...EMPTY,
					itemId: id,
					status: 'ready',
					loaded: true,
					comments: page.comments,
					next: page.skip + page.comments.length,
					total: page.total,
					canComment: page.canComment,
					strictAuditing: page.strictAuditing,
				});
			})
			.catch(error => {
				if (cancelled) return;
				const refusal = commentRefusal(error, 'load');
				if (refusal.kind === 'gone' || refusal.kind === 'off') {
					setState({ ...EMPTY, itemId: id, status: 'hidden', hidden: refusal.kind, error: refusal.text });
					return;
				}
				setState(prev => ({ ...(prev.itemId === id ? prev : { ...EMPTY, itemId: id }), status: 'error', error: refusal.text }));
			});
		return () => { cancelled = true; };
	}, [active, id, reloadKey]);

	// An answer for another item than the one on show is not this one's.
	const mine = state.itemId === id ? state : EMPTY;

	const reload = useCallback(() => setReloadKey(key => key + 1), []);

	const readMore = useCallback(async () => {
		const have = stateRef.current;
		if (have.itemId !== currentRef.current || have.next >= have.total) return false;
		setState(prev => ({ ...prev, loadingMore: true }));
		try {
			const page = await listItemComments(have.itemId, { skip: have.next, limit: COMMENTS_PAGE_SIZE });
			if (currentRef.current !== have.itemId) return false;
			setState(prev => (prev.itemId !== have.itemId ? prev : {
				...prev,
				loadingMore: false,
				comments: mergePage(prev.comments, page.comments),
				next: page.skip + page.comments.length,
				total: page.total,
				canComment: page.canComment,
				strictAuditing: page.strictAuditing,
			}));
			return page.comments.length > 0;
		} catch (error) {
			if (currentRef.current !== have.itemId) return false;
			const refusal = commentRefusal(error, 'load');
			setState(prev => (prev.itemId !== have.itemId ? prev : { ...prev, loadingMore: false, error: refusal.text }));
			return false;
		}
	}, []);

	const loadMore = useCallback(async () => { await readMore(); }, [readMore]);

	/** Every page that is still to come (so a comment just added lands in its place). */
	const loadRest = useCallback(async () => {
		for (let i = 0; i < MAX_PAGES; i += 1) {
			const s = stateRef.current;
			if (s.itemId !== currentRef.current || s.next >= s.total) return;
			// eslint-disable-next-line no-await-in-loop
			if (!(await readMore())) return;
		}
	}, [readMore]);

	// One write at a time: a second press while one is on its way is ignored.
	const run = useCallback(async (kind, rowId, work, context = {}) => {
		if (busyRef.current) return { ok: false, kind: 'retry', text: '' };
		const forItem = currentRef.current;
		busyRef.current = { kind, id: rowId };
		setBusy({ kind, id: rowId });
		try {
			const result = await work(forItem);
			return result;
		} catch (error) {
			const refusal = commentRefusal(error, kind, context);
			if (currentRef.current === forItem) {
				if (refusal.kind === 'gone' || refusal.kind === 'off') {
					setState({ ...EMPTY, itemId: forItem, status: 'hidden', hidden: refusal.kind, error: refusal.text });
				} else if (refusal.kind === 'audit_changed') {
					// The mode moved under the person: show it as it is now at once,
					// and read the list again (a deleted row appears or goes).
					if (typeof refusal.strictAuditing === 'boolean') {
						setState(prev => (prev.itemId !== forItem ? prev : { ...prev, strictAuditing: refusal.strictAuditing }));
					}
					setReloadKey(key => key + 1);
				} else if (refusal.kind === 'refresh') {
					setReloadKey(key => key + 1);
				}
			}
			return { ok: false, kind: refusal.kind, text: refusal.text, quiet: refusal.quiet === true, strictAuditing: refusal.strictAuditing };
		} finally {
			busyRef.current = null;
			setBusy(null);
		}
	}, []);

	const add = useCallback(body => run('add', null, async forItem => {
		const comment = await addItemComment(forItem, body);
		if (currentRef.current !== forItem) return { ok: true, comment };
		const has = () => stateRef.current.comments.some(entry => idOf(entry) === idOf(comment));
		const append = prev => (prev.itemId !== forItem || prev.comments.some(entry => idOf(entry) === idOf(comment)) ? prev : {
			...prev,
			comments: [...prev.comments, comment],
			total: prev.total + 1,
			next: prev.next + 1,
		});
		const have = stateRef.current;
		if (have.next >= have.total) {
			// Everything is on screen: the new one goes last.
			setState(append);
		} else {
			// Pages are still to come, and the new comment is the newest of
			// them all: bring them in, so it lands after them, not before.
			await loadRest();
			if (!has() && currentRef.current === forItem) setState(append);
		}
		return { ok: true, comment };
	}), [run, loadRest]);

	const edit = useCallback((commentId, body) => run('edit', commentId, async forItem => {
		const comment = await editItemComment(forItem, commentId, body);
		if (currentRef.current === forItem) {
			setState(prev => (prev.itemId !== forItem ? prev : {
				...prev,
				comments: prev.comments.map(entry => (idOf(entry) === normalizeId(commentId) ? { ...entry, ...comment } : entry)),
			}));
		}
		return { ok: true, comment };
	}), [run]);

	const remove = useCallback(commentId => run('delete', commentId, async forItem => {
		// The mode the person was shown goes with every delete (KPR-789): the
		// API refuses it when the card's mode is not that any more.
		const comment = await deleteItemComment(forItem, commentId, stateRef.current.strictAuditing);
		if (currentRef.current === forItem) {
			const key = normalizeId(commentId);
			setState(prev => {
				if (prev.itemId !== forItem) return prev;
				if (!prev.strictAuditing) {
					// Off: it leaves the list, and the server's count with it.
					return {
						...prev,
						comments: prev.comments.filter(entry => idOf(entry) !== key),
						total: Math.max(0, prev.total - 1),
						next: Math.max(0, prev.next - 1),
					};
				}
				// On: the row stays as a placeholder (who, when), without its text.
				return {
					...prev,
					comments: prev.comments.map(entry => {
						if (idOf(entry) !== key) return entry;
						const { body, ...rest } = { ...entry, ...comment, deleted: true };
						return rest;
					}),
				};
			});
		}
		return { ok: true, comment };
	}, {
		// A note from before comments (KPR-801) is refused in its own words.
		migrated: Boolean(stateRef.current.comments.find(entry => idOf(entry) === normalizeId(commentId))?.migrated),
	}), [run]);

	// What a person is shown: with strict auditing off, no deleted row at all.
	const comments = useMemo(
		() => (mine.strictAuditing ? mine.comments : mine.comments.filter(entry => entry?.deleted !== true)),
		[mine.comments, mine.strictAuditing]
	);

	return {
		status: active ? (mine.status === 'idle' ? 'loading' : mine.status) : 'idle',
		loaded: mine.loaded,
		hidden: mine.hidden,
		error: mine.error,
		comments,
		total: mine.total,
		hasMore: mine.next < mine.total,
		loadingMore: mine.loadingMore,
		canComment: mine.canComment,
		strictAuditing: mine.strictAuditing,
		busy,
		reload,
		loadMore,
		add,
		edit,
		remove,
	};
}
